// FULLY DYNAMIC, PAN-INDIA VERSION

const OVERPASS_URL = "https://overpass-api.de/api/interpreter";
const OVERPASS_TIMEOUT_MS = 3000; // fail fast - this is a "nice to have", never worth a 40s hang

/**
 * Find real nearby postal pincodes around a coordinate using OSM data.
 * Returns at most the 4 closest distinct pincodes, sorted nearest-first.
 *
 * If Overpass is slow, down, or blocked (which it can be - it's a shared
 * public server), this gives up after OVERPASS_TIMEOUT_MS and returns an
 * empty array rather than hanging the whole search. A search should never
 * be blocked waiting on a "nice to have" nearby-pincode enrichment step.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {number} radiusKm - default 3km
 * @returns {Promise<string[]>} up to 4 nearest unique pincodes
 */
export const getNearbyPincodes = async (lat, lng, radiusKm = 3) => {
  if (typeof lat !== "number" || typeof lng !== "number") {
    console.warn("⚠️ getNearbyPincodes: invalid lat/lng", lat, lng);
    return [];
  }

  const MAX_RESULTS = 4;
  const radiusMeters = Math.round(radiusKm * 1000);

  // "out center tags" gives us a lat/lon for ways/relations too (not just
  // nodes), so we can measure real distance and pick the closest ones.
  const query = `
    [out:json][timeout:25];
    nwr["addr:postcode"](around:${radiusMeters},${lat},${lng});
    out center tags qt 300;
  `;

  console.log(
    `\n🚀 getNearbyPincodes called with: lat=${lat}, lng=${lng}, radius=${radiusKm}km`
  );

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), OVERPASS_TIMEOUT_MS);

  try {
    const response = await fetch(OVERPASS_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: query,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      console.warn(`⚠️ Overpass API returned status ${response.status}`);
      return [];
    }

    const data = await response.json();

    // Keep only the closest occurrence of each distinct pincode
    const closestByPincode = new Map();

    (data.elements || []).forEach((el) => {
      const pc = el.tags?.["addr:postcode"];
      if (!pc) return;

      const elLat = el.lat ?? el.center?.lat;
      const elLng = el.lon ?? el.center?.lon;
      if (elLat == null || elLng == null) return;

      const pincode = String(pc).trim();
      const distance = haversineKm(lat, lng, elLat, elLng);

      const existing = closestByPincode.get(pincode);
      if (!existing || distance < existing) {
        closestByPincode.set(pincode, distance);
      }
    });

    const results = Array.from(closestByPincode.entries())
      .sort((a, b) => a[1] - b[1]) // nearest first
      .slice(0, MAX_RESULTS)
      .map(([pincode]) => pincode);

    console.log(`📦 Found ${results.length} nearby pincode(s) (closest ${MAX_RESULTS} max):`, results);
    return results;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      console.warn(`⚠️ Overpass timed out after ${OVERPASS_TIMEOUT_MS}ms - skipping nearby pincodes`);
    } else {
      console.warn("⚠️ Overpass fetch failed:", error.message);
    }
    return [];
  }
};

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Last-resort fallback for finding the MAIN pincode of a specific point,
 * used only when both India Post's name directory AND Nominatim's own
 * postcode tag have failed for a location (e.g. "Pitampura" - a popular
 * colloquial name that isn't itself a post office name).
 *
 * Deliberately kept SMALL-RADIUS (1-5km, never district-wide). A wrong
 * "district-level" fallback is what caused Pitampura to resolve to
 * Badli's pincode earlier - matching an entire district to one office is
 * fundamentally the wrong shape of answer. Finding the physically nearest
 * real tagged postcode within a few km is a much safer last resort.
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<string|null>}
 */
export const findNearestPincode = async (lat, lng) => {
  const radiiToTry = [1, 3, 5]; // km - stays local, never balloons out to district-scale

  for (const radiusKm of radiiToTry) {
    const radiusMeters = Math.round(radiusKm * 1000);
    const query = `
      [out:json][timeout:10];
      nwr["addr:postcode"](around:${radiusMeters},${lat},${lng});
      out center tags qt 100;
    `;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), OVERPASS_TIMEOUT_MS);

    try {
      const response = await fetch(OVERPASS_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: query,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) continue;

      const data = await response.json();
      const candidates = (data.elements || [])
        .map((el) => {
          const pc = el.tags?.["addr:postcode"];
          const elLat = el.lat ?? el.center?.lat;
          const elLng = el.lon ?? el.center?.lon;
          if (!pc || elLat == null || elLng == null) return null;
          return { pincode: String(pc).trim(), distance: haversineKm(lat, lng, elLat, elLng) };
        })
        .filter(Boolean)
        .sort((a, b) => a.distance - b.distance);

      if (candidates.length > 0) {
        console.log(
          `📮 Nearest pincode via Overpass (${radiusKm}km search): ${candidates[0].pincode} (${candidates[0].distance.toFixed(2)}km away)`
        );
        return candidates[0].pincode;
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.warn(`⚠️ findNearestPincode error at ${radiusKm}km:`, error.message);
    }
  }

  console.log("❌ No pincode found within 5km either - genuinely no data here");
  return null;
};